import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { useTrip } from '../../hooks/useTrip';
import { useAuth } from '../../hooks/useAuth';
import { useDesign } from '../../designs';
import { trackPaymentCompleted } from '../../config/analytics';
import { Settings, CalendarDays, Plane, DollarSign, CheckCircle } from 'lucide-react';
import OrnamentalDivider from '../../components/ui/OrnamentalDivider';
import DesignButton from '../../components/ui/DesignButton';
import ItineraryTab from './ItineraryTab';
import TravelTab from './TravelTab';
import ExpensesTab from './ExpensesTab';
import SettingsModal from './SettingsModal';
import PostPaymentTour from './PostPaymentTour';
import { isShareCodeTaken, saveCustomSlug } from '../../services/trips';

type TabId = 'itinerary' | 'travel' | 'expenses';

export default function TripScreen({ tripIdOverride }: { tripIdOverride?: string } = {}) {
  const params = useParams<{ tripId: string }>();
  const tripId = tripIdOverride || params.tripId;
  const { data: trip, isLoading } = useTrip(tripId || '');
  const { user } = useAuth();
  const design = useDesign();
  const d = design.tokens;
  const fs = design.typography.fontSize;
  const displayFont = `'${design.typography.displayFont}', serif`;
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [activeTab, setActiveTab] = useState<TabId>('itinerary');
  const [itineraryKey, setItineraryKey] = useState(0);
  const [showSettings, setShowSettings] = useState(false);
  const [showPaymentConfirm, setShowPaymentConfirm] = useState(false);
  const [showTour, setShowTour] = useState(false);
  const [isDetailView, setIsDetailView] = useState(false);
  const [shareLinkCopied, setShareLinkCopied] = useState(false);
  const [editingShareSlug, setEditingShareSlug] = useState(false);
  const [shareSlug, setShareSlug] = useState('');
  const [shareSlugSaving, setShareSlugSaving] = useState(false);
  const [shareSlugError, setShareSlugError] = useState('');

  useEffect(() => {
    if (searchParams.get('payment') === 'success') {
      if (tripId) trackPaymentCompleted(tripId);
      const tourKey = `krewe_tour_seen_${tripId}`;
      if (!localStorage.getItem(tourKey)) {
        setShowPaymentConfirm(true);
      }
      searchParams.delete('payment');
      setSearchParams(searchParams, { replace: true });
    }
  }, [searchParams, setSearchParams, tripId]);

  if (isLoading) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: d.bgPrimary }}>
        <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: '7px', fontWeight: 800, letterSpacing: '9px', textTransform: 'uppercase', color: 'rgba(200,146,58,0.18)' }}>KREWE</span>
      </div>
    );
  }

  if (!trip) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: d.bgPrimary }}>
        <p style={{ fontFamily: "'Raleway', sans-serif", color: d.textSecondary }}>Trip not found</p>
        <button onClick={() => navigate('/')} style={{ fontFamily: "'Raleway', sans-serif", color: d.accent, background: 'none', border: 'none', cursor: 'pointer', marginTop: '16px' }}>Back to dashboard</button>
      </div>
    );
  }

  const meta = trip.meta;
  if (!meta) {
    return (
      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '100vh', backgroundColor: d.bgPrimary }}>
        <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: '7px', fontWeight: 800, letterSpacing: '9px', textTransform: 'uppercase', color: 'rgba(200,146,58,0.18)' }}>KREWE</span>
      </div>
    );
  }

  const currentUid = user?.uid || '';

  // Resolve which member this user maps to — either direct membership
  // (planner's own UID, or self-added crew) or via claimedBy on a placeholder.
  const claimedMemberUid = (() => {
    // Direct membership (planner logged in, or self-added crew member)
    if (trip.members?.[currentUid]) return currentUid;
    // Check if any placeholder member was claimed by this anonymous UID
    for (const [uid, member] of Object.entries(trip.members || {})) {
      if (member.claimedBy === currentUid) return uid;
    }
    return null;
  })();

  // Planner if: direct planner role, OR claimed a member with planner role
  const isPlanner = !!(
    trip.members?.[currentUid]?.role === 'planner' ||
    (claimedMemberUid && trip.members?.[claimedMemberUid]?.role === 'planner')
  );

  // canEdit: true for planners and claimed crew members
  const canEdit = isPlanner || !!claimedMemberUid;

  // Format date range for hero — uppercase month
  const formatDateHero = () => {
    if (!meta.startDate || !meta.endDate) return '';
    const start = new Date(meta.startDate + 'T00:00:00');
    const end = new Date(meta.endDate + 'T00:00:00');
    const fmt = (d: Date) => d.toLocaleDateString('en-US', { month: 'long', day: 'numeric' });
    return `${fmt(start)} – ${fmt(end)}`.toUpperCase();
  };

  const tabConfig: { id: TabId; label: string; Icon: React.FC<{ size: number; strokeWidth: number; color: string }> }[] = [
    { id: 'itinerary', label: 'Itinerary', Icon: CalendarDays },
    { id: 'travel', label: 'Travel', Icon: Plane },
    { id: 'expenses', label: 'Expenses', Icon: DollarSign },
  ];

  return (
    <div style={{
      maxWidth: '480px', margin: '0 auto', minHeight: '100vh',
      backgroundColor: d.bgPrimary, paddingBottom: '80px',
      fontFamily: "'Raleway', sans-serif", color: d.textPrimary,
    }}>
      {/* ═══ HERO ═══ */}
      {!isDetailView && (
        <div style={{ padding: '0 26px', position: 'relative', textAlign: 'center' }}>
          {/* Settings gear — visible to planner and claimed crew only */}
          {canEdit && (
            <button onClick={() => setShowSettings(true)} style={{
              position: 'absolute', top: '16px', right: '26px', background: 'none', border: 'none',
              cursor: 'pointer', zIndex: 2, display: 'flex', alignItems: 'center',
            }}>
              <Settings size={20} strokeWidth={1.5} color={d.textSecondary} />
            </button>
          )}

          {/* KREWE wordmark — barely visible watermark */}
          <div style={{
            fontFamily: "'Raleway', sans-serif", fontSize: fs.wordmark, fontWeight: 800,
            letterSpacing: '9px', textTransform: 'uppercase',
            color: 'rgba(200,146,58,0.18)',
            paddingTop: '24px', marginBottom: '20px',
          }}>KREWE</div>

          {/* Occasion line — editable, hidden if empty */}
          {meta.occasion && (
            <div style={{
              fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700,
              letterSpacing: '4px', textTransform: 'uppercase',
              color: 'rgba(200,146,58,0.40)',
              marginBottom: '10px',
            }}>{meta.occasion}</div>
          )}

          {/* Trip title — dynamic sizing to prevent long names from wrapping off screen */}
          {(() => {
            const nameLen = (meta.tripName || '').length;
            const titleSize = nameLen <= 15 ? '34px' : nameLen <= 25 ? '28px' : '24px';
            return (
              <h1 style={{
                fontFamily: displayFont, fontSize: titleSize, fontWeight: 900,
                lineHeight: 1.0, color: d.textPrimary, margin: '0 0 8px',
              }}>{meta.tripName}</h1>
            );
          })()}

          {/* City */}
          <div style={{
            fontFamily: displayFont, fontSize: '18px', fontWeight: 400,
            fontStyle: 'italic', color: d.accent, marginBottom: '12px',
          }}>{meta.destination}</div>

          {/* Date line with flanking rules */}
          {formatDateHero() && (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              gap: '12px', marginBottom: '18px',
            }}>
              <div style={{ width: '24px', height: '1px', backgroundColor: 'rgba(200,146,58,0.25)' }} />
              <span style={{
                fontFamily: displayFont, fontSize: '13px', fontWeight: 600,
                letterSpacing: '2px', color: 'rgba(237,228,216,0.60)',
              }}>{formatDateHero()}</span>
              <div style={{ width: '24px', height: '1px', backgroundColor: 'rgba(200,146,58,0.25)' }} />
            </div>
          )}

          {/* Diamond ornamental divider */}
          <OrnamentalDivider style={{ marginTop: 0, marginBottom: '24px' }} />
        </div>
      )}

      {/* Payment confirmation screen */}
      {showPaymentConfirm && (
        <div style={{
          position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
          backgroundColor: d.bgPrimary, zIndex: 100,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center',
          padding: '32px',
        }}>
          <CheckCircle size={48} strokeWidth={1.5} color={d.success} />
          <p style={{
            fontFamily: "'Raleway', sans-serif", fontSize: fs.sectionHeader, fontWeight: 700,
            letterSpacing: '3.5px', textTransform: 'uppercase',
            color: d.success, margin: '20px 0 12px', textAlign: 'center',
          }}>Payment Confirmed</p>
          <p style={{
            fontFamily: displayFont, fontSize: '18px', fontWeight: 600,
            fontStyle: 'italic', color: d.textPrimary,
            textAlign: 'center', margin: '0 0 32px', lineHeight: 1.4,
          }}>Your trip is ready to share with the crew.</p>
          <button onClick={() => { setShowPaymentConfirm(false); setShowTour(true); }} style={{
            fontFamily: "'Raleway', sans-serif", fontSize: fs.button, fontWeight: 700,
            letterSpacing: '2.5px', textTransform: 'uppercase',
            color: d.accent, background: 'none', border: 'none',
            cursor: 'pointer', padding: '8px 16px',
          }}>SEE WHAT'S NEXT →</button>
        </div>
      )}

      {/* Post-payment tour overlay */}
      {showTour && meta && (
        <PostPaymentTour tripId={tripId || ''} meta={meta} onDismiss={() => {
          setShowTour(false);
          if (tripId) localStorage.setItem(`krewe_tour_seen_${tripId}`, 'true');
        }} />
      )}

      {/* Share CTA — build phase only */}
      {isPlanner && meta.phase === 'build' && !isDetailView && (
        <div style={{ padding: '0 26px', marginBottom: '20px' }}>
          <DesignButton tier={2} fullWidth onClick={() => navigate(`/trip/${tripId}/paywall`)}>
            Share with the Crew
          </DesignButton>
        </div>
      )}

      {/* Share link bar — shared phase, itinerary tab only (planner utility, not trip content) */}
      {meta.phase === 'shared' && !isDetailView && activeTab === 'itinerary' && (() => {
        const currentSlug = meta.customSlug || meta.shareCode;
        const shareUrl = `${window.location.origin}/t/${currentSlug}`;
        const handleCopyShareLink = async () => {
          try {
            await navigator.clipboard.writeText(shareUrl);
          } catch {
            const ta = document.createElement('textarea');
            ta.value = shareUrl;
            ta.style.position = 'fixed';
            ta.style.opacity = '0';
            document.body.appendChild(ta);
            ta.select();
            document.execCommand('copy');
            document.body.removeChild(ta);
          }
          setShareLinkCopied(true);
          setTimeout(() => setShareLinkCopied(false), 2000);
        };
        const handleSaveShareSlug = async () => {
          const cleaned = shareSlug.trim().toLowerCase().replace(/[^a-z0-9-]/g, '');
          if (!cleaned || cleaned === currentSlug) {
            setEditingShareSlug(false);
            setShareSlug(currentSlug);
            return;
          }
          setShareSlugSaving(true);
          setShareSlugError('');
          try {
            const taken = await isShareCodeTaken(cleaned);
            if (taken) { setShareSlugError('Taken'); setShareSlugSaving(false); return; }
            const oldSlug = meta.customSlug || meta.shareCode;
            await saveCustomSlug(tripId || '', cleaned, oldSlug, meta.shareCode);
            setEditingShareSlug(false);
          } catch (err) {
            console.error('Failed to save slug:', err);
            setShareSlugError('Error');
          } finally {
            setShareSlugSaving(false);
          }
        };
        return (
          <div style={{
            margin: '0 26px 20px',
            padding: '12px 16px',
            backgroundColor: d.bgSurface,
            border: `1px solid ${d.border}`,
            borderRadius: '2px',
          }}>
            {editingShareSlug ? (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, color: d.textSecondary, flexShrink: 0 }}>/t/</span>
                <input type="text" value={shareSlug}
                  onChange={(e) => { setShareSlug(e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '')); setShareSlugError(''); }}
                  onKeyDown={(e) => e.key === 'Enter' && handleSaveShareSlug()}
                  autoFocus
                  style={{
                    flex: 1, fontFamily: "'Raleway', sans-serif", fontSize: fs.detail,
                    color: d.textPrimary, backgroundColor: d.bgSurface,
                    border: `1px solid ${shareSlugError ? d.negative : 'rgba(200,146,58,0.30)'}`,
                    borderRadius: '2px', padding: '4px 8px', outline: 'none', minWidth: 0,
                  }}
                />
                {shareSlugError && <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.metaLabel, color: d.negative }}>{shareSlugError}</span>}
                <button onClick={handleSaveShareSlug} disabled={shareSlugSaving} style={{
                  fontFamily: "'Raleway', sans-serif", fontSize: fs.button, fontWeight: 700,
                  letterSpacing: '2px', textTransform: 'uppercase',
                  color: d.confirmed, background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0,
                }}>{shareSlugSaving ? '...' : 'Save'}</button>
                <button onClick={() => { setEditingShareSlug(false); setShareSlug(currentSlug); setShareSlugError(''); }} style={{
                  fontSize: fs.bodyInput, color: d.textSecondary, background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0,
                }}>×</button>
              </div>
            ) : (
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                <span style={{
                  fontFamily: "'Raleway', sans-serif", fontSize: fs.detail, fontWeight: 300,
                  color: d.textSecondary, flex: 1,
                  overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                }}>{shareUrl}</span>
                <button onClick={() => { setEditingShareSlug(true); setShareSlug(currentSlug); }} style={{
                  fontFamily: "'Raleway', sans-serif", fontSize: fs.button, fontWeight: 700,
                  letterSpacing: '2px', textTransform: 'uppercase',
                  color: d.accent, background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0,
                }}>Edit</button>
                <div style={{ width: '1px', height: '14px', backgroundColor: d.border, flexShrink: 0 }} />
                <button onClick={handleCopyShareLink} style={{
                  fontFamily: "'Raleway', sans-serif", fontSize: fs.button, fontWeight: 700,
                  letterSpacing: '2px', textTransform: 'uppercase',
                  color: shareLinkCopied ? d.confirmed : d.accent,
                  background: 'none', border: 'none', cursor: 'pointer', flexShrink: 0,
                }}>{shareLinkCopied ? 'Copied' : 'Copy'}</button>
              </div>
            )}
          </div>
        );
      })()}

      {/* ═══ TAB CONTENT ═══ */}
      {activeTab === 'itinerary' && (
        <ItineraryTab key={itineraryKey} trip={trip} tripId={tripId || ''} isPlanner={isPlanner} canEdit={canEdit} currentUid={currentUid} claimedMemberUid={claimedMemberUid} onDetailView={setIsDetailView} />
      )}
      {activeTab === 'travel' && (
        <TravelTab trip={trip} tripId={tripId || ''} isPlanner={isPlanner} canEdit={canEdit} currentUid={currentUid} claimedMemberUid={claimedMemberUid} />
      )}
      {activeTab === 'expenses' && (
        <ExpensesTab trip={trip} tripId={tripId || ''} canEdit={canEdit} currentUid={currentUid} />
      )}

      {/* ═══ BOTTOM NAV ═══ */}
      <div style={{
        position: 'fixed', bottom: 0, left: '50%', transform: 'translateX(-50%)',
        width: '100%', maxWidth: '480px', backgroundColor: d.bgPrimary,
        borderTop: `1px solid ${d.border}`,
        display: 'flex', justifyContent: 'space-around',
        padding: '8px 0 16px', zIndex: 50,
      }}>
        {tabConfig.map((tab) => {
          const isActive = activeTab === tab.id;
          const iconColor = isActive ? d.accent : d.textSecondary;
          return (
            <button
              key={tab.id}
              onClick={() => {
                setIsDetailView(false);
                if (isDetailView) setItineraryKey(k => k + 1);
                setActiveTab(tab.id);
              }}
              style={{
                background: 'none', border: 'none', cursor: 'pointer',
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px',
                opacity: isActive ? 1 : 0.6, position: 'relative',
              }}
            >
              {isActive && (
                <div style={{
                  position: 'absolute', top: '-8px', left: '50%', transform: 'translateX(-50%)',
                  width: '32px', height: '3px', borderRadius: '2px',
                  backgroundColor: d.accent, opacity: 0.5,
                }} />
              )}
              <tab.Icon size={20} strokeWidth={1.5} color={iconColor} />
              <span style={{ fontFamily: "'Raleway', sans-serif", fontSize: fs.button, fontWeight: 400, letterSpacing: '1px', textTransform: 'uppercase', color: iconColor }}>
                {tab.label}
              </span>
            </button>
          );
        })}
      </div>

      {showSettings && canEdit && (
        <SettingsModal trip={trip} tripId={tripId || ''} isPlanner={isPlanner} claimedMemberUid={claimedMemberUid} currentUid={currentUid} onClose={() => setShowSettings(false)} />
      )}
    </div>
  );
}
